import React from "react";

import { bindActionCreators } from "@reduxjs/toolkit";
import { connect } from "react-redux";

import * as BusbudError from "@busbud/busbud-errors";

import { useAppContext } from "@app/helpers/hooks";
import type { AppDispatch } from "@app/modules/results/store/configure-store";
import type { SentryClient } from "@app/services/sentry-facade";
import type { TranslatorService } from "@app/services/translator";
import type { ResultsReduxState } from "@app/types/results-redux-types";
import type { SearchInfo } from "@app/types/search-types";

import type { ErrorModalButtonType } from "../../../helpers/api-error";
import type { SupportedLocale } from "@config/locales";

import * as ApiError from "../../../helpers/api-error";
import { closeModal } from "../../../modules/results/actions";
import * as ErrorTracking from "../../../tracking/error-tracking";
import { AlertDialog } from "../../alert-dialog";
import { reloadOutboundPage } from "../utils/reload-outbound-page";

type ResultsContextId = "cart";

interface OwnProps {
  error: Error | BusbudError.BusbudError | null;
  context_id?: ResultsContextId;
  onAction?: () => void;
  is_async_cart?: boolean;
}

interface ReduxProps {
  closeModal: () => any;
  search: SearchInfo;
}

const _isTicketCurrentlyUnavailableError = (
  bb_err: BusbudError.BusbudError | null,
  context: ResultsContextId | undefined
) => {
  return (
    context === "cart" && bb_err && bb_err instanceof BusbudError.GeneralError
  );
};

const _getErrorModalOptions = (
  bb_err: BusbudError.BusbudError | null,
  context: ResultsContextId | undefined,
  sentry: SentryClient | null,
  translator: TranslatorService,
  locale: SupportedLocale
): ApiError.ErrorModalConfig => {
  // Override generic error for cart
  if (_isTicketCurrentlyUnavailableError(bb_err, context)) {
    return {
      name: "TicketCurrentlyUnavailable",
      title: translator.t("!route.errors.add-cart-invalid.title"),
      text: translator.t("!route.errors.add-cart-invalid.text"),
      button_config: {
        primary_button: {
          label: translator.t("!error.GeneralError.button.cta"),
          action_name: ApiError.ERROR_HANDLING_ACTIONS.CHECKOUT_RESTART_SEARCH
        }
      }
    };
  }

  return ApiError.getErrorModalConfig(translator, sentry, bb_err, locale);
};

const ErrorMessage: React.FC<ReduxProps & OwnProps> = props => {
  const { sentry, tracker, translator, urlBuilder, locale } = useAppContext();
  const {
    search,
    context_id: context,
    error,
    closeModal,
    is_async_cart
  } = props;

  const bb_error = ApiError.toBusbudError(error);

  const config = _getErrorModalOptions(
    ApiError.toUserVisibleError(error),
    context,
    sentry,
    translator,
    locale
  );

  const onAction = (button_type: ErrorModalButtonType) => {
    tracker.track(ErrorTracking.clickedErrorButton(button_type));

    if (
      config.button_config &&
      config.button_config[`${button_type}_button`]?.action_name &&
      props.onAction
    ) {
      props.onAction();
      return;
    }

    if (
      bb_error &&
      (bb_error instanceof BusbudError.BookingDepartureExpired ||
        bb_error instanceof BusbudError.BookingDepartureNoLongerAvailable ||
        bb_error instanceof BusbudError.CartDepartureExpired ||
        bb_error instanceof BusbudError.CartDepartureNoLongerAvailable ||
        bb_error instanceof BusbudError.DepartureNotFound ||
        bb_error instanceof BusbudError.NoSeatsAvailable ||
        bb_error instanceof BusbudError.ReferencedModelDoesNotExist ||
        _isTicketCurrentlyUnavailableError(bb_error, context))
    ) {
      return reloadOutboundPage(urlBuilder, search, is_async_cart);
    }

    return closeModal();
  };

  return (
    <AlertDialog
      title={config.title}
      message={config.text}
      actionHandler={onAction}
      primary_button={{
        label: translator.t("!route.errors.default-button-label")
      }}
      {...config.button_config}
    />
  );
};

const mapStateToProps = (state: ResultsReduxState): any => {
  return {
    search: state.search
  };
};

const mapDispatchToProps = (dispatch: AppDispatch) =>
  bindActionCreators({ closeModal }, dispatch);

const ErrorMessageComponent = connect<
  ReduxProps,
  {},
  OwnProps,
  ResultsReduxState
>(
  mapStateToProps,
  mapDispatchToProps
)(ErrorMessage);

export { ErrorMessageComponent as ErrorMessage };
